import _ from 'lodash'

const absMax = Number.MAX_VALUE
const absMin = (-1 * Number.MAX_VALUE)


/**
 * Sets the min/max at the beginning and end of the array if those values don't
 * currently exists using the default min/max provided.
 */
function ensureMinMax(arr, defaultMin, defaultMax) {
	//assume the min/max is the autoMin/Max passed in
	let min = defaultMin
	let max = defaultMax
	//determine if user input was higher than the autoMin/max defined
	//if so then make the autoMin/Max the absolute lowest/highest number
	let values = _.filter(arr, val => _.isFinite(val))
	if (values.length) {
		values.sort((a, b) => a - b)
		let userMax = values[values.length-1]
		let userMin = values[0]
		if (userMax > defaultMax) {
			max = absMax
		}
		if (userMin < defaultMin) {
			min = absMin
		}
	}

	//set any missing min/max
	if (!_.isFinite(arr[0])) {
		arr[0] = min
	}
	if (!_.isFinite(arr[arr.length-1])) {
		arr[arr.length-1] = max
	}
}

/**
 * Returns data on any groups of missing thresholds in the array.  A missing threshold
 * is defined as an element in the array that cannot be resolved as a number.  For
 * each group of missing thresholds, the start and end index where the thresholds
 * are missing are recorded as well as the number of missing in that group.  For
 * example, given the array [0, null, null, 3, null, 6].  There are two groups
 * of missing thresholds, {start: 1, end: 2, missing: 2} and
 * { start:4, end:4: missing: 1} are returned as an array
 */
function findMissingThresholds(arr) {
	let missingThresholds = []
	let missingInfo
	for (let i = 1; i < arr.length - 1; i++) {
		if (!_.isFinite(arr[i])) {
			if (!missingInfo) {
				missingInfo = {start: i, end: i+1, missing: 1}
				missingThresholds.push(missingInfo)
			} else {
				missingInfo.missing++
				missingInfo.end = i + 1
			}
		} else {
			missingInfo = null
		}
	}
	return missingThresholds
}


class Thresholds {
	constructor(opts) {
		this.autoMin = _.isFinite(opts.min) ? opts.min : absMin
		this.autoMax = _.isFinite(opts.max) ? opts.max : absMax

		//no clone, we want the changes to apply
		this.thresholds = opts.thresholds || []
		this.bounds = {}
		if (_.isFinite(opts.segments)) {
			// one more threshold than there are segments
			this.resize(opts.segments + 1)
		} else {
			this._calculateBounds()
		}
	}

	resize(size) {
		if (_.isArray(size)) {
			this.thresholds = [].concat(size)
			this.resize(size.length)
			return
		}

		if (size < 0) { return }
		let numAdd = size - this.thresholds.length
		if (numAdd > 0) {
			for (let i=0; i<numAdd; i++) {
				this.thresholds.push(null)
			}
		} else {
			let numRemove = this.thresholds.length - size
			this.thresholds = _.dropRight(this.thresholds, numRemove)
		}
		this._calculateBounds()
	}

	_calculateBounds() {
		_.each(this.thresholds, (val, index) => {
			let prevIndex = index-1
			while (!_.isFinite(this.thresholds[prevIndex]) && prevIndex > -1) {
				prevIndex--
			}
			let nextIndex = index+1
			while (!_.isFinite(this.thresholds[nextIndex]) && nextIndex < this.thresholds.length) {
				nextIndex++
			}
			this.bounds[index] = {max: this.thresholds[nextIndex], min: this.thresholds[prevIndex]}
		})
	}

	bounds(index) {
		return this.bounds[index]
	}

	/**
	 * get/set value at index
	 */
	value(value, index) {
		if (_.isNil(index)) {
			return this.thresholds[value]
		}

		if (index > this.thresholds.length-1) { return }
		this.thresholds[index] = value

		this._calculateBounds()
	}

	/**
	 * Returns the values in the thresholds, calculateMissing is true then also
	 * resolve any missing bounds using the min/max for the
	 * lower/upper bounds respectively and the midpoint between the previous/next
	 * threshold for any missing thresholds in the middle
	 */
	values(calculateMissing = true) {
		let result = [].concat(this.thresholds)
		if (!calculateMissing) {
			return result
		}

		if (result.length == 1) {
			if (!_.isFinite(result[0])) {
				return [this.autoMax]
			} else {
				return result
			}
		}

		//assume the min/max is the autoMin/Max passed in
		ensureMinMax(result, this.autoMin, this.autoMax)

		//compute any missing middle thresholds
		let missing = findMissingThresholds(result)

		//for each group of missing thresholds, compute the threshold value in
		//equal sizes
		_.each(missing, (missingInfo) => {
			let lowerBound = result[missingInfo.start - 1]
			let upperBound = result[missingInfo.end]
			let equalPortion = (upperBound - lowerBound) / (missingInfo.missing + 1)
			result[missingInfo.start] = lowerBound + equalPortion
			for (let index = missingInfo.start + 1; index < missingInfo.end; index++) {
				result[index] = result[index - 1] + equalPortion
			}
		})

		return result
	}
}

export default Thresholds
Thresholds.__filename__ = 'components/threshold-colors/thresholds.js'
